import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowRight, Users, CheckCircle2, Loader2 } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import { partnersAPI } from '../utils/api';
import { trackCpaPartnerLeadSubmit } from '../utils/analytics';

const INTEREST_OPTIONS = [
  'Referring clients to MoneyFeed',
  'Pro CPA partner (annual)',
  'Business partner (custom annual)',
  'Co-branded / white-label (exploratory)',
  'Just exploring',
];

const emptyForm = {
  firm_name: '', contact_name: '', business_email: '', phone: '',
  location: '', approx_clients: '', partnership_interest: INTEREST_OPTIONS[0],
  message: '', company: '',
};

export default function PartnersAccountants() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setErrorMsg('');
    setStatus('submitting');
    try {
      await partnersAPI.submitAccountantLead(form);
      trackCpaPartnerLeadSubmit(form.partnership_interest);
      setStatus('success');
    } catch (err) {
      const code = err?.response?.status;
      setErrorMsg(
        code === 429
          ? 'You have sent a few requests quickly. Please wait a minute and try again.'
          : 'Something went wrong submitting your inquiry. Please check your details and try again.'
      );
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#F5F3FF] to-[#D1FAE5]" data-testid="partners-accountants-page">
      <Seo
        title="CPA Partner Program Inquiry | MoneyFeed"
        description="Apply to the MoneyFeed CPA partner program."
        path="/partners/accountants"
        robots="index,follow"
      />
      <LandingHeader />

      <section className="max-w-2xl mx-auto px-4 sm:px-8 pt-12 pb-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/10 rounded-full mb-5">
            <Users className="h-4 w-4 text-[#7C3AED]" />
            <span className="text-xs font-bold text-[#7C3AED] uppercase tracking-wider">CPA Partner Program</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#064E3B] leading-tight mb-3">
            Become a MoneyFeed CPA Partner
          </h1>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            Tell us about your firm and we'll be in touch about partnership opportunities for eligible Pro and Business subscriptions.
          </p>
        </div>

        {status === 'success' ? (
          <div className="bg-white rounded-3xl shadow-md p-8 sm:p-10 text-center" data-testid="partner-success">
            <CheckCircle2 className="h-14 w-14 text-[#10B981] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#064E3B] mb-2">Inquiry received</h2>
            <p className="text-gray-600 mb-6">
              Thanks for your interest in the MoneyFeed CPA partner program. Our team will review your details and reach out.
            </p>
            <Link to="/demo">
              <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-full px-7 py-5" data-testid="partner-success-demo">
                See How MoneyFeed Works <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-md p-6 sm:p-8 space-y-5" data-testid="partner-form" noValidate>
            {/* Honeypot: hidden from users, catches bots */}
            <input
              type="text" name="company" tabIndex={-1} autoComplete="off"
              value={form.company} onChange={set('company')}
              className="hidden" aria-hidden="true"
            />

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="firm_name">Firm name</Label>
                <Input id="firm_name" required maxLength={200} value={form.firm_name} onChange={set('firm_name')} placeholder="Smith & Co. CPAs" data-testid="firm-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="contact_name">Contact name</Label>
                <Input id="contact_name" required maxLength={200} value={form.contact_name} onChange={set('contact_name')} placeholder="Jane Smith" data-testid="contact-name-input" className="mt-1.5" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="business_email">Business email</Label>
                <Input id="business_email" type="email" required maxLength={254} value={form.business_email} onChange={set('business_email')} placeholder="jane@firm.com" data-testid="business-email-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="phone">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input id="phone" type="tel" maxLength={40} value={form.phone} onChange={set('phone')} placeholder="+1 555 000 1234" data-testid="phone-input" className="mt-1.5" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="location">City / State</Label>
                <Input id="location" required maxLength={200} value={form.location} onChange={set('location')} placeholder="Austin, TX" data-testid="location-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="approx_clients">Approx. business clients <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input id="approx_clients" maxLength={100} value={form.approx_clients} onChange={set('approx_clients')} placeholder="e.g. 40" data-testid="approx-clients-input" className="mt-1.5" />
              </div>
            </div>

            <div>
              <Label htmlFor="partnership_interest">Partnership interest</Label>
              <select
                id="partnership_interest"
                value={form.partnership_interest}
                onChange={set('partnership_interest')}
                data-testid="partnership-interest-select"
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]"
              >
                {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div>
              <Label htmlFor="message">Message <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Textarea id="message" maxLength={2000} rows={4} value={form.message} onChange={set('message')} placeholder="Tell us a bit about your firm and what you're looking for." data-testid="message-input" className="mt-1.5" />
            </div>

            {status === 'error' && (
              <p className="text-sm text-red-600" data-testid="partner-error">{errorMsg}</p>
            )}

            <Button type="submit" disabled={status === 'submitting'} className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-full py-6 text-base" data-testid="partner-submit-btn">
              {status === 'submitting' ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Submitting…</>) : (<>Submit partnership inquiry <ArrowRight className="h-4 w-4 ml-1" /></>)}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Referral commissions are subject to partner eligibility and approved written terms. Co-branded and white-label arrangements are exploratory and not yet available as products.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
